// React
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useTable, useSortBy } from "react-table";
import imageFallback from 'assets/images/image-not-available.png';
// External libraries
import { Table } from 'react-bootstrap';
import isEmpty from 'lodash/isEmpty';
import Lightbox from "react-awesome-lightbox";
// Styles
import './styles.scss';

/**
 * Displays a custom table based on React Bootstrap table
 * @param {Object} props.data - The data to be displayed in the table
 * @param {Object} props.headers - The headers of the data in the table
 * @param {Object} props.className - The min height of each row
 */
export default function CustomTable(props) {
    const className = props.className ? props.className : '';
    let headers = props.headers ? props.headers : [];
    let bEmptyHeaders = headers.length === 0;
    let propDefaultSortBy = props.defaultSortBy ?? {};

    // If the custom table has no headers, create an array filled with whitespace based on the data length
    if (bEmptyHeaders && props.data.length !== 0) {
        headers = Array.from({length: props.data[0].length}).map(() => '');
    }

    // Memoised the column header definitions
    const columns = useMemo(() => {
        return headers.map((objHeader, index) => {
            let strText = objHeader.name ? objHeader.name.toLowerCase() : objHeader;
            let strAccessor = objHeader.id ? objHeader.id : `${index}`;

            let objHeaderDef = {
                Header: strText === 'action' ? '' : () => (<span className={objHeader.className ?? ''}>{strText}</span>),
                accessor: strAccessor,
                disableSortBy:
                    props.disableSort === true
                        ? true
                        : objHeader.disableSort === true
                            ? true
                            : (strText === '' || strText === 'action'),
            };

            // We define a custom sorting function for date columns
            if (strAccessor === 'dateEntered') {
                objHeaderDef = Object.assign(objHeaderDef, {
                    sortType:  (a, b) => {
                        const a1 = new Date(a.values[strAccessor]).getTime();
                        const b1 = new Date(b.values[strAccessor]).getTime();

                        if(a1 > b1)
                            return 1;
                        else if(a1 < b1)
                            return -1;
                        else
                            return 0;
                    }
                });
            }

            return objHeaderDef;
        });
    }, [props.headers, props.disableSort]);

    // Memoised the row data definitions
    const data = useMemo(() => {
        return props.data.map((objData) => {
            return Object.assign({}, objData);
        })
    }, [props.data]);

    // Memoised the default sort by
    const defaultSortBy = useMemo(() => {
        return {
            sortBy: [ propDefaultSortBy ],
        }
    }, [props.defaultSortBy]);

    // Declare the table options
    let objTableOptions = {
        columns, data
    };

    // Only add the initial state if there is a default sort by
    if (!isEmpty(propDefaultSortBy)) {
        objTableOptions['initialState'] = defaultSortBy;
    }

    // Create the react table
    const reactTableInstance = useTable(objTableOptions, useSortBy);

    const { getTableProps, headerGroups, rows, prepareRow } = reactTableInstance;

    return (
        <Table className={className} striped hover {...getTableProps()}>
            {!bEmptyHeaders ?
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                // Add the sorting props to control sorting
                                <th className="col-2" {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>{column.render("Header")}</div>
                                        {/* Is this column sortable? (i.e. disableSortBy = false) */}
                                        {column.canSort
                                            ? // Add a sort direction indicator
                                                <div>
                                                    {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? <FontAwesomeIcon icon={faSortDown} className="sort-icon"/>
                                                        : <FontAwesomeIcon icon={faSortUp} className="sort-icon"/>
                                                    :
                                                    <FontAwesomeIcon icon={faSort} className="sort-icon"/>
                                                    }
                                                </div>
                                            : ''
                                        }
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead> : null
            }
            <tbody>
                {rows.map((row, i) => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()} className={ row.original.isChild ? 'child-product' : '' }>
                            {row.cells.map((cell) => {
                                const cellStyle = row.original.isChild ? { verticalAlign: 'top' } : {};
                                return <td {...cell.getCellProps()} style={cellStyle}>
                                    {cell.render("Cell")}
                                    {props.lightBox && props.lightBox.index === cell.value ?
                                        <Lightbox
                                            className="table-img"
                                            image={props.lightBox.url}
                                            alt={imageFallback}
                                            onClose={(evt) => {
                                                props.lightBox.onClose(evt);
                                            }}
                                            allowZoom={props.lightBox.allowZoom ?? false}
                                            allowRotate={props.lightBox.allowRotate ?? false}
                                            allowReset={props.lightBox.allowReset ?? false}
                                        /> : null
                                    }
                                </td>;
                            })}
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
}
